import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { fetchPeerStats } from "../../lib/api";

const COLORS = {
  success: "#2C5C43",
  warning: "#E2A03F",
  danger: "#A84632",
  muted: "#5C665D",
};

export default function BenchmarkCard({
  global: globalBench,
  region: regionBench,
  regionCode,
}) {
  const [scope, setScope] = useState(
    regionBench && regionBench.scope === "region" ? "region" : "global"
  );
  const [peerStats, setPeerStats] = useState(null);

  useEffect(() => {
    if (!globalBench?.label) return;
    fetchPeerStats({
      industry: globalBench.label,
      region: scope === "region" ? regionCode : undefined,
    })
      .then(setPeerStats)
      .catch(() => setPeerStats(null));
  }, [globalBench?.label, scope, regionCode]);

  const benchmark = scope === "region" && regionBench ? regionBench : globalBench;
  const regionAvailable = regionBench && regionBench.scope === "region";

  const rows = [
    { key: "gross_margin", label: "Gross margin", suffix: "%" },
    { key: "ebitda_margin", label: "EBITDA margin", suffix: "%" },
    { key: "operating_margin", label: "Operating margin", suffix: "%" },
    { key: "net_margin", label: "Net margin", suffix: "%" },
    { key: "current_ratio", label: "Current ratio", suffix: "x" },
    { key: "quick_ratio", label: "Quick ratio", suffix: "x" },
    { key: "dscr", label: "DSCR", suffix: "x" },
    { key: "debt_to_equity", label: "Debt / Equity", suffix: "x" },
    { key: "debt_to_ebitda", label: "Debt / EBITDA", suffix: "x" },
    { key: "interest_coverage", label: "Interest coverage", suffix: "x" },
    { key: "dso", label: "DSO", suffix: "d" },
    { key: "dio", label: "DIO", suffix: "d" },
    { key: "dpo", label: "DPO", suffix: "d" },
    { key: "cash_conversion_cycle", label: "Cash conv. cycle", suffix: "d" },
    { key: "ocf_margin", label: "OCF margin", suffix: "%" },
    { key: "fcf_margin", label: "FCF margin", suffix: "%" },
  ];
  const cmps = benchmark.comparisons || {};

  return (
    <Card className="bg-white border-[#E5E0D8] shadow-none" data-testid="benchmark-card">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardDescription className="text-xs uppercase tracking-[0.18em] text-[#5C665D]">
              Industry context · {scope === "region" ? `In-region (${regionCode})` : "Global"}
            </CardDescription>
            <CardTitle className="font-serif-display text-2xl text-[#1F382E]">
              {benchmark.label}
            </CardTitle>
            <p className="text-sm text-[#5C665D] leading-relaxed mt-1">{benchmark.notes}</p>
          </div>
          <div
            className="inline-flex p-1 bg-[#F7F5F0] border border-[#E5E0D8] rounded-md no-print"
            role="tablist"
            data-testid="benchmark-scope-toggle"
          >
            <button
              type="button"
              onClick={() => setScope("global")}
              data-testid="benchmark-scope-global"
              className={`px-3.5 py-1.5 rounded-sm text-xs transition-all ${
                scope === "global"
                  ? "bg-[#1F382E] text-[#F7F5F0]"
                  : "text-[#5C665D] hover:text-[#1F382E]"
              }`}
            >
              Global
            </button>
            <button
              type="button"
              onClick={() => regionAvailable && setScope("region")}
              disabled={!regionAvailable}
              data-testid="benchmark-scope-region"
              className={`px-3.5 py-1.5 rounded-sm text-xs transition-all ${
                scope === "region"
                  ? "bg-[#1F382E] text-[#F7F5F0]"
                  : regionAvailable
                    ? "text-[#5C665D] hover:text-[#1F382E]"
                    : "text-[#D4C9B6] cursor-not-allowed"
              }`}
              title={
                regionAvailable
                  ? "In-region benchmark"
                  : "No regional overlay yet — contribute data to unlock"
              }
            >
              In-region {regionCode && regionCode !== "GLOBAL" ? `· ${regionCode}` : ""}
            </button>
          </div>
        </div>
        {peerStats && (
          <div
            className="mt-2 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#5C665D]"
            data-testid="peer-stats-callout"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#C26D5C]" />
            {peerStats.medians
              ? `${peerStats.n} community contributions · medians live`
              : `${peerStats.n} of ${peerStats.min_n} contributions for community medians`}
          </div>
        )}
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3">
          {rows.map((r) => {
            const c = cmps[r.key];
            if (!c) return null;
            return <BenchmarkRow key={r.key} label={r.label} suffix={r.suffix} c={c} />;
          })}
        </div>
        <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-xs text-[#5C665D]">
          <LegendDot color={COLORS.success} label="Top quartile / above peer" />
          <LegendDot color="#5e7a4d" label="Mid-range / healthy" />
          <LegendDot color={COLORS.warning} label="Below median" />
          <LegendDot color={COLORS.danger} label="Bottom of range" />
        </div>
      </CardContent>
    </Card>
  );
}

function LegendDot({ color, label }) {
  return (
    <span className="flex items-center gap-2">
      <span className="h-2 w-2 rounded-full" style={{ background: color }} />
      {label}
    </span>
  );
}

function BenchmarkRow({ label, suffix, c }) {
  const positionMap = {
    above: { color: COLORS.success, word: "Above peer" },
    high: { color: COLORS.success, word: "Top of range" },
    mid: { color: "#5e7a4d", word: "Mid-range" },
    low: { color: COLORS.warning, word: "Below median" },
    below: { color: COLORS.danger, word: "Below peer range" },
    na: { color: COLORS.muted, word: "—" },
  };
  const lowerBetterLabels = ["Debt / Equity", "Debt / EBITDA", "DSO", "DIO", "Cash conv. cycle"];
  const isLowerBetter = lowerBetterLabels.includes(label);
  let m = positionMap[c.position] || positionMap.na;
  if (isLowerBetter && c.position !== "na") {
    const flipped = { above: "below", high: "low", low: "high", below: "above", mid: "mid" };
    m = positionMap[flipped[c.position] || c.position];
  }
  const value = c.value === null ? "—" : `${c.value}${suffix}`;
  const range = c.value === null ? "" : `peer ${c.low}–${c.high}${suffix} · median ${c.median}${suffix}`;
  return (
    <div className="flex items-start justify-between gap-3 py-1.5 border-b border-[#F0EDE5] last:border-b-0">
      <div className="min-w-0">
        <p className="text-sm text-[#1A1A1A]">{label}</p>
        <p className="text-[11px] text-[#5C665D] mt-0.5 truncate">{range}</p>
      </div>
      <div className="text-right shrink-0">
        <p className="font-serif-display text-lg text-[#1F382E] tabular-nums leading-none">{value}</p>
        <p className="text-[11px] mt-1 inline-flex items-center gap-1.5" style={{ color: m.color }}>
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: m.color }} />
          {m.word}
        </p>
      </div>
    </div>
  );
}
